/**
 * Sidebar constants — module icon defaults, mobile rail grouping, module-type
 * object mapping, plugin-type mapping, and the new-module catalog.
 *
 * Glyph divergence vs types/modules.ts is intentionally preserved pending a
 * design-gated PR — do not reconcile icons/labels here.
 */

import React from 'react';

export const DEFAULT_MODULE_ICONS: Record<string, string> = {
  task_list: '✅',
  note_grid: '📝',
  event_list: '📆',
  contact_list: '👥',
  places: '📍',
  place_list: '📍',
  drive: '📁',
  notes: '📝',
  stat_list: '📊',
  kanban: '📋',
  kanban_board: '🗂️',
  crm: '🤝',
  client_list: '🤝',
};

/** Rail-style module grouping for mobile sidebar (matches desktop SidebarRail) */
export const MOBILE_RAIL_GROUPS: Array<{ key: string; label: string; types: string[]; icon: React.ReactNode }> = [
  { key: 'email', label: 'Email', types: ['email'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,6 12,13 2,6" />
    </svg>
  )},
  { key: 'calendar', label: 'Calendar', types: ['event_list'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )},
  { key: 'tasks', label: 'Kanban', types: ['task_list'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="18" rx="1" /><rect x="17" y="3" width="5" height="18" rx="1" />
    </svg>
  )},
  { key: 'contacts', label: 'Contacts', types: ['contact_list', 'client_list'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )},
  { key: 'files', label: 'Drive', types: ['drive', 'file_viewer', 'note_grid'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )},
  { key: 'places', label: 'Places', types: ['places', 'place_list'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  )},
  { key: 'pages', label: 'Studio', types: ['pages', 'report', 'html_viewer'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  )},
  { key: 'health', label: 'Health', types: ['health'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  )},
  { key: 'channels', label: 'Channels', types: ['channels'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )},
  { key: 'automations', label: 'Automations', types: ['automations', 'agents'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
    </svg>
  )},
  { key: 'notes', label: 'Notes', types: ['notes'], icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v12l-4 4H4z" /><path d="M16 20v-4h4" /><path d="M8 9h8M8 13h5" />
    </svg>
  )},
];

export const MODULE_TYPE_TO_OBJECT: Record<string, string | undefined> = {
  task_list: 'task', event_list: 'event', drive: 'asset', notes: 'note',
  contact_list: 'contact', places: 'place', place_list: 'place', stat_list: 'stat',
  kanban: 'task', kanban_board: 'task', crm: 'contact', client_list: 'contact',
};

export const PLUGIN_TYPE_MAP: Record<string, string> = {
  'app-analytics': 'app-analytics',
  'mixpanel': 'app-analytics',
  'shopify-crm': 'shopify-crm',
  'news': 'news',
};

/** New-module catalog entry shape. */
export interface NewModuleCatalogEntry {
  emoji: string;
  label: string;
  type: string;
  desc: string;
  /** If true, only shown in team workspaces (hidden in personal picker). */
  teamOnly?: boolean;
  /** If true, module is Xavior-native and must never be linked to a provider. */
  nativeOnly?: boolean;
}

/** Core modules shown in the new-module wizard (personal workspaces). */
export const NEW_MODULE_CORE_CATALOG: NewModuleCatalogEntry[] = [
  { emoji: '📧', label: 'Email', type: 'email', desc: 'Use AI to manage your inbox' },
  { emoji: '📅', label: 'Calendar', type: 'event_list', desc: 'Manage and track events' },
  { emoji: '📁', label: 'Drive', type: 'drive', desc: 'Files, photos, and documents' },
  { emoji: '👤', label: 'Contacts', type: 'contact_list', desc: 'People and organizations' },
  { emoji: '✅', label: 'Kanban', type: 'task_list', desc: 'Manage tasks in stages' },
  { emoji: '📝', label: 'Notes', type: 'notes', desc: 'Plain-text notes and snippets' },
  { emoji: '📍', label: 'Places', type: 'place_list', desc: 'Save favorite places, restaurants etc' },
  { emoji: '💬', label: 'Channels', type: 'channels', desc: 'Team group chat with topic channels', teamOnly: true },
];

/** Individual (personal) modules shown in the team workspace picker. */
export const NEW_MODULE_INDIVIDUAL_CATALOG: NewModuleCatalogEntry[] = [
  { emoji: '📧', label: 'Email', type: 'email', desc: 'Your personal Gmail or Outlook inbox' },
  { emoji: '📅', label: 'Calendar', type: 'event_list', desc: 'Your personal Google Calendar or Outlook events' },
  { emoji: '📁', label: 'Drive', type: 'drive', desc: 'Your personal files and documents' },
];

/** Shared team modules shown in the team workspace picker. */
export const NEW_MODULE_TEAM_CATALOG: NewModuleCatalogEntry[] = [
  { emoji: '💬', label: 'Channels', type: 'channels', desc: 'Team messaging with topic channels' },
  { emoji: '✅', label: 'Team Kanban', type: 'task_list', desc: 'Shared task board for the team' },
  { emoji: '👤', label: 'Team Contacts', type: 'contact_list', desc: 'Shared contact list — Xavior-native' },
  { emoji: '📅', label: 'Team Calendar', type: 'event_list', desc: 'Shared team calendar — not linked to Google or Outlook', nativeOnly: true },
  { emoji: '📁', label: 'Team Drive', type: 'drive', desc: 'Shared file storage — Xavior-native' },
  { emoji: '🎨', label: 'Studio', type: 'pages', desc: 'Reports, decks, and HTML pages' },
];

/** Plugin modules shown in the new-module wizard. */
export const NEW_MODULE_PLUGIN_CATALOG: NewModuleCatalogEntry[] = [
  { emoji: '📈', label: 'Mixpanel', type: 'mixpanel', desc: 'Product analytics' },
  { emoji: '🛍️', label: 'Shopify CRM', type: 'shopify-crm', desc: 'Customer orders and revenue' },
];
